import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COLORS = ["emerald", "blue", "amber", "purple", "green", "red", "slate"];

export default function Statuses() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("slate");
  const [reqFu, setReqFu] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/lead-statuses?include_inactive=true").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/lead-statuses", { name, color_token: color, requires_followup: reqFu });
      toast.success("Status created");
      setOpen(false); setName(""); setColor("slate"); setReqFu(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const toggle = async (slug, active) => {
    try {
      await api.patch(`/lead-statuses/${slug}`, { active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Lead Statuses</h1>
          <p className="text-muted-foreground text-sm mt-1">Configure your pipeline stages. Fully customizable.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-status-button" className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1.5" />Add Status</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader><DialogTitle>New Status</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-muted-foreground">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="status-name-input" className="bg-slate-900/60 border-slate-700 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Color</Label>
                <Select value={color} onValueChange={setColor}>
                  <SelectTrigger data-testid="status-color-select" className="bg-slate-900/60 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {COLORS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="flex items-center justify-between rounded-lg border border-slate-800 p-3">
                <span className="text-sm">Requires follow-up</span>
                <Switch checked={reqFu} onCheckedChange={setReqFu} data-testid="status-requires-followup" />
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} data-testid="save-status-button" className="bg-primary text-primary-foreground">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass rounded-xl p-4">
        {!items ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((s) => (
              <div key={s.slug} data-testid={`status-card-${s.slug}`} className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 flex items-center justify-between">
                <div>
                  <StatusBadge status={s.name} colorToken={s.color_token} />
                  <div className="text-[11px] text-muted-foreground mt-2 space-x-2">
                    {s.requires_followup && <span>follow-up</span>}
                    {s.triggers_email && <span>· email</span>}
                    {s.is_terminal && <span>· terminal</span>}
                  </div>
                </div>
                <Switch checked={s.active} onCheckedChange={(v) => toggle(s.slug, v)} data-testid={`toggle-status-${s.slug}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
